// 安全地创建 PC 端消息 API
let pcMessageApi = null;
let pcCenterMessageApi = null;
let isInitialized = false;
let isCenterInitialized = false;

// 初始化 naive-ui API
const initPcMessageApi = async (center = false) => {
	if (!import.meta.client) return;

	try {
		const { createDiscreteApi } = await import("naive-ui/es/discrete");
		if (center) {
			if (isCenterInitialized) return;
			const { message } = createDiscreteApi(["message"], {
				messageProviderProps: {
					placement: "top",
					containerStyle: {
						top: "50%",
						transform: "translateY(-50%)",
					},
				},
			});
			pcCenterMessageApi = message;
			isCenterInitialized = true;
			return;
		}
		if (isInitialized) return;
		const { message } = createDiscreteApi(["message"]);
		pcMessageApi = message;
		isInitialized = true;
	} catch (error) {
		console.error("Failed to initialize naive-ui message API:", error);
	}
};

const mobileToastOptions = (msg, center) => ({
	message: String(msg),
	duration: 2000,
	className: "app-mobile-success-toast",
	wordBreak: "break-word",
	...(center ? { position: "middle" } : {}),
});

// 移动端消息方法（假设使用 vant 或其他移动端 UI 库）
const mobileMessageMap = {
	success: (msg, center) => {
		try {
			if (typeof showSuccessToast === "function") {
				showSuccessToast(mobileToastOptions(msg, center));
			} else {
				console.log(`[Success]: ${msg}`);
			}
		} catch (e) {
			console.error("Failed to show mobile success toast:", e);
		}
	},
	error: (msg, center) => {
		try {
			if (typeof showFailToast === "function") {
				showFailToast(mobileToastOptions(msg, center));
			} else {
				console.error(`[Error]: ${msg}`);
			}
		} catch (e) {
			console.error("Failed to show mobile error toast:", e);
		}
	},
	info: (msg, center) => {
		try {
			if (typeof showToast === "function") {
				showToast(mobileToastOptions(msg, center));
			} else {
				console.log(`[Info]: ${msg}`);
			}
		} catch (e) {
			console.error("Failed to show mobile info toast:", e);
		}
	},
	warning: (msg, center) => {
		try {
			if (typeof showToast === "function") {
				showToast(mobileToastOptions(msg, center));
			} else {
				console.warn(`[Warning]: ${msg}`);
			}
		} catch (e) {
			console.error("Failed to show mobile warning toast:", e);
		}
	},
	default: (msg, center) => {
		try {
			if (typeof showToast === "function") {
				showToast(mobileToastOptions(msg, center));
			} else {
				console.log(`[Message]: ${msg}`);
			}
		} catch (e) {
			console.error("Failed to show mobile default toast:", e);
		}
	},
};

const getPcMessageApi = center => (center ? pcCenterMessageApi : pcMessageApi);

// PC 端消息方法
const pcMessageMap = {
	success: async (msg, center) => {
		try {
			await initPcMessageApi(center);
			getPcMessageApi(center)?.success(msg) || console.log(`[Success]: ${msg}`);
		} catch (e) {
			console.error("Failed to show PC success message:", e);
		}
	},
	error: async (msg, center) => {
		try {
			await initPcMessageApi(center);
			getPcMessageApi(center)?.error(msg) || console.error(`[Error]: ${msg}`);
		} catch (e) {
			console.error("Failed to show PC error message:", e);
		}
	},
	info: async (msg, center) => {
		try {
			await initPcMessageApi(center);
			getPcMessageApi(center)?.info(msg) || console.log(`[Info]: ${msg}`);
		} catch (e) {
			console.error("Failed to show PC info message:", e);
		}
	},
	warning: async (msg, center) => {
		try {
			await initPcMessageApi(center);
			getPcMessageApi(center)?.warning?.(msg) || console.warn(`[Warning]: ${msg}`);
		} catch (e) {
			console.error("Failed to show PC warning message:", e);
		}
	},
	default: async (msg, center) => {
		try {
			await initPcMessageApi(center);
			getPcMessageApi(center)?.info(msg) || console.log(`[Message]: ${msg}`);
		} catch (e) {
			console.error("Failed to show PC default message:", e);
		}
	},
};

/**
 * 显示跨端消息提示
 * @param {string} msg 要显示的消息内容
 * @param {string} type 消息类型，默认为 'default'
 * @param {{ center?: boolean }} [options] center 为 true 时在视口垂直居中显示
 */
const showMessage = async (msg, type = "default", options = {}) => {
	if (!msg) {
		console.warn("showMessage: empty message provided");
		return;
	}

	const center = Boolean(options?.center);

	try {
		// 安全地获取Nuxt应用实例
		let isMobile = false;
		try {
			if (import.meta.client) {
				// 尝试获取Nuxt应用实例
				const nuxtApp = useNuxtApp();
				isMobile = nuxtApp.$isMobile || false;
			}
		} catch (nuxtError) {
			// 如果无法获取Nuxt实例，使用浏览器API检测
			if (import.meta.client && typeof window !== "undefined") {
				isMobile =
					window.innerWidth <= 768 ||
					/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
			}
		}

		if (isMobile) {
			const showMethod = mobileMessageMap[type] || mobileMessageMap.default;
			showMethod(msg, center);
		} else {
			const showMethod = pcMessageMap[type] || pcMessageMap.default;
			await showMethod(msg, center);
		}
	} catch (error) {
		console.error("Failed to show message:", error);
		// 降级处理
		console.log(`[${type}]: ${msg}`);
	}
};

export default showMessage;
